import FJLocalStore from '../../../store/FJLocalStore';
import styles from './FJProgressStatus.module.less';
import PropTypes from 'prop-types';
import FJDeleteIcon from '../../../assets/svg/delete-x.svg?react';
import FJConstant from '../../../constants/FJConstant';
import FJButton from '../FJButton/FJButton';
import FJResourceItemLoading from '../FJResourceItemLoading/FJResourceItemLoading';
import classNames from 'classnames';
import ReloadIcon from '../../../assets/svg/reload.svg?react';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (minutes > 0) {
        return `${minutes}min${remainingSeconds}s`;
    } else {
        return `${remainingSeconds}s`;
    }
};

const FJProgressStatus = ({
    progress,
    allTime,
    cancelProgress,
    status,
    retry,
    thumbnailUrl,
    width,
    height,
    isShowControl = true,
    disabled = false,
    onSourceReload,
    showEstimateTime = true,
}) => {
    const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
    const [loadingStyle, setLoadingStyle] = useState({});
    const loadRef = useRef(null);

    const updateLoadingStyle = useCallback(() => {
        if (status === FJConstant.LOADING) {
            const loadBox = loadRef.current.getBoundingClientRect();

            const aspectRatio = width / height;

            let loadingWidth = 0;
            let loadingHeight = 0;

            if (aspectRatio > 1) {
                loadingWidth = loadBox.width;
                loadingHeight = loadBox.width / aspectRatio;
            } else {
                loadingWidth = loadBox.height * aspectRatio;
                loadingHeight = loadBox.height;
            }

            if (loadingWidth > loadBox.width) {
                loadingWidth = loadBox.width;
                loadingHeight = loadingWidth / aspectRatio;
            }

            if (loadingHeight > loadBox.height) {
                loadingHeight = loadBox.height;
                loadingWidth = loadingHeight * aspectRatio;
            }

            setLoadingStyle({
                width: `${loadingWidth}px`,
                height: `${loadingHeight}px`,
            });
        }
    }, [status, width, height]);

    useEffect(() => {
        updateLoadingStyle();

        window.addEventListener('resize', updateLoadingStyle);
        return () => {
            window.removeEventListener('resize', updateLoadingStyle);
        };
    }, [updateLoadingStyle]);

    const thumbnailBox = useMemo(() => {
        return (
            <img
                className={classNames(styles.thumbnail, {
                    [styles.thumbnail_show]:
                        thumbnailLoaded && status !== FJConstant.LOADING && status !== FJConstant.SOURCE_LOADING_ERROR,
                })}
                src={thumbnailUrl}
                alt='thumbnail'
                onLoad={() => setThumbnailLoaded(true)}
            />
        );
    }, [thumbnailUrl, thumbnailLoaded, status]);

    const maskBox = useMemo(() => {
        return <>{status !== FJConstant.LOADING && <div className={styles.mask} />}</>;
    }, [status]);

    return (
        <div
            className={classNames(styles.container, {
                [styles.not_show_control]: !isShowControl,
                [styles.disabled]: disabled,
            })}
        >
            <div className={classNames(styles.content, { [styles.bg]: status !== FJConstant.LOADING })}>
                {thumbnailBox}
                {maskBox}

                {(status === FJConstant.START || status === FJConstant.GENERATING) && (
                    <>
                        <div className={styles.title}>
                            <div className={styles.title_text}>{FJLocalStore._('processing')}</div>

                            {showEstimateTime ? (
                                <div className={styles.title_time}>
                                    {FJLocalStore._('estimate-time').replace('xxx', formatTime(allTime))}
                                </div>
                            ) : null}

                            <div className={styles.title_delete} onClick={cancelProgress}>
                                <FJDeleteIcon className={styles.delete_icon} />
                            </div>
                        </div>
                        <div className={styles.progress}>
                            <div className={styles.line} style={{ width: `${progress}%` }} />
                        </div>
                    </>
                )}

                {status === FJConstant.LOADING && (
                    <div className={styles.loading} ref={loadRef}>
                        <FJResourceItemLoading className={styles.loading_box} style={loadingStyle} />
                    </div>
                )}

                {status === FJConstant.SOURCE_LOADING_ERROR && (
                    <div className={styles.source_error}>
                        <ReloadIcon className={styles.reload_icon} onClick={onSourceReload} />
                    </div>
                )}

                {status === FJConstant.ERROR && (
                    <div className={styles.error}>
                        <div className={styles.error_text}>{FJLocalStore._('unknown-error-occurred-please-retry')}</div>
                        <FJButton text={FJLocalStore._('retry')} onClick={retry} className={styles.error_btn} />
                    </div>
                )}
            </div>
        </div>
    );
};

FJProgressStatus.propTypes = {
    progress: PropTypes.number,
    allTime: PropTypes.number,
    cancelProgress: PropTypes.func,
    status: PropTypes.string,
    retry: PropTypes.func,
    thumbnailUrl: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    isShowControl: PropTypes.bool,
    disabled: PropTypes.bool,
    onSourceReload: PropTypes.func,
};

export default FJProgressStatus;
